/**
 * opsRoutes.js — Single Source of Truth para rotas do módulo de Governança/Ops
 *
 * REGRA DE OURO:
 *   sidebar path = route path = overview button path = breadcrumb path
 *
 * Nunca escrever paths manualmente em componentes.
 * Sempre importar deste arquivo:
 *   import { R } from '../navigation/opsRoutes';
 *   navigate(R.investidores)
 *   <Link to={R.marketingHub}>
 *
 * @version 2.0.0 — Flat structure for ease of use
 * @date 2026-05-25
 */

// ═══════════════════════════════════════════════════════════════════════════
// ROTAS CANÔNICAS (paths reais registrados no router)
// ═══════════════════════════════════════════════════════════════════════════

export const R = Object.freeze({
  // Root & Overview
  root: '/governance',
  overview: '/governance/overview',

  // Usuários & Acessos
  usuarios: '/governance/usuarios',

  // Autorizações
  autorizacoes: '/governance/autorizacoes',

  // RH
  rh: '/governance/rh',
  rhOverview: '/governance/rh/overview',
  rhPeople: '/governance/rh/pessoas',
  rhCompensation: '/governance/rh/remuneracao',
  rhHeadcountPlan: '/governance/rh/planejamento',
  rhPermissions: '/governance/rh/permissoes',
  rhFinancial: '/governance/rh/financeiro',

  // Comercial
  comercialPlanos: '/governance/comercial/planos',
  comercialLeads: '/governance/comercial/leads',
  comercialAccessRequests: '/governance/comercial/solicitacoes',
  clientes: '/governance/clientes',
  marketplace: '/governance/marketplace',
  usageDashboard: '/governance/usage',
  clienteDetail: '/governance/clientes', // append /:id at usage

  // Financeiro
  fin: '/governance/fin',
  finCustos: '/governance/fin/custos',
  finReceita: '/governance/fin/receita',
  finDespesas: '/governance/fin/despesas',
  finResultado: '/governance/fin/resultado',
  finPrecificacao: '/governance/fin/precificacao',

  // Investidores
  investidores: '/governance/investidores',

  // Marketing
  marketingHub: '/governance/marketing/hub',
  marketingSeo: '/governance/marketing/seo',
  marketingCreator: '/governance/marketing/creator',
  marketingStudyBuilder: '/governance/marketing/study-builder',
  marketingAnalyticsTool: '/governance/marketing/analytics-tool',
  marketingContentStudio: '/governance/marketing/content-studio',
  marketingPlanner30Dias: '/governance/marketing/planner-30-dias',
  editorialEngine: '/governance/editorial-engine',

  // TI / Operações
  tiControlTower: '/governance/ti/control-tower',
  tiSLOs:         '/governance/ti/slos',
  tiAgentes:      '/governance/ti/agentes',
  tiMonitoring:   '/governance/ti/monitoring',     // Fase 1 Observabilidade
  tiIncidentes:   '/governance/ti/incidentes', // kept for legacy redirect only

  // Projetos / Exec
  execPlanner: '/governance/exec/planner',
  execBuilder: '/governance/exec/builder',
  execBrain: '/governance/exec/brain',
  competeMinas: '/governance/compete-minas',
  // Página genérica read-only de projeto (código via ?projeto=<project_code>)
  projeto: '/governance/exec/projeto',

  // Purview
  purview: '/governance/purview',

  // Content (legacy, redirects to marketing)
  contentStudio: '/governance/content-studio',
});

// ═══════════════════════════════════════════════════════════════════════════
// REDIRECTS LEGADOS — paths antigos que devem redirecionar para canônicos
// Usado em OpsApp.jsx e App.js para gerar <Navigate> automaticamente
// ═══════════════════════════════════════════════════════════════════════════

export const LEGACY_REDIRECTS = Object.freeze([
  // Investidores
  { from: '/investors', to: R.investidores },
  { from: '/investidores', to: R.investidores },
  { from: '/governance/investors', to: R.investidores },

  // Marketing
  { from: '/marketing', to: R.marketingHub },
  { from: '/marketing/overview', to: R.marketingHub },
  { from: '/marketing/hub', to: R.marketingHub },
  { from: '/governance/marketing', to: R.marketingHub },
  { from: '/governance/marketing/overview', to: R.marketingHub },
  { from: '/governance/pesquisa', to: R.marketingHub },
  { from: '/governance/marketing/pesquisa', to: R.marketingHub },
  { from: '/governance/research', to: R.marketingHub },
  { from: '/governance/content', to: R.marketingHub },

  // Comercial
  { from: '/comercial', to: R.comercialPlanos },
  { from: '/comercial/overview', to: R.comercialPlanos },
  { from: '/comercial/planos', to: R.comercialPlanos },
  { from: '/governance/comercial', to: R.comercialPlanos },

  // TI
  { from: '/ti/control-tower', to: R.tiControlTower },
  { from: '/ti/observability', to: R.tiSLOs },
  { from: '/ti/parser-monitor', to: R.tiControlTower },
  { from: '/ti/agentes', to: R.tiAgentes },
  { from: '/ti/incidentes', to: R.tiSLOs },
  { from: '/governance/ti/observability', to: R.tiSLOs },
  { from: '/governance/ti/parser-monitor', to: R.tiControlTower },
  { from: '/governance/ti/incidentes', to: R.tiSLOs },

  // Financeiro
  { from: '/fin', to: R.fin },

  // RH
  { from: '/hr', to: R.rh },
  { from: '/governance/hr', to: R.rh },
  { from: '/governance/rh/people', to: R.rhPeople },
  { from: '/governance/rh/compensation', to: R.rhCompensation },
  { from: '/governance/rh/headcount-plan', to: R.rhHeadcountPlan },
  { from: '/governance/rh/permissions', to: R.rhPermissions },
  { from: '/governance/rh/financial-integration', to: R.rhFinancial },

  // Exec
  { from: '/exec/planner', to: R.execPlanner },

  // Ops (legacy)
  { from: '/pesquisa', to: R.marketingHub },
]);

// Alias for backward compat with previous version of this file
export const OPS_ROUTES = { governance: R, legacy: LEGACY_REDIRECTS };
export default R;
